import { z } from "zod";
import { EmptyPayloadSchema, OkDataSchema } from "./common.js";
import { IsoDateTimeSchema } from "./primitives.js";

/**
 * Gateway-minted agent-team identifier. The `team_` prefix keeps the value
 * self-describing in logs and payloads where a bare id would read as a Slack
 * workspace id, which is a different namespace entirely.
 */
export const AgentTeamIdSchema = z
  .string()
  .min(6)
  .max(128)
  .regex(/^team_[A-Za-z0-9]+$/);

/**
 * Field bounds, single-sourced here because three layers enforce them: this
 * wire schema, the Gateway store that trims before checking, and the Hub form
 * that reports the failure before the round-trip. A cap that drifts between
 * them makes one layer accept what another rejects.
 */
export const AGENT_TEAM_NAME_MAX = 80;
export const AGENT_TEAM_DESCRIPTION_MAX = 1000;

/**
 * Team name as stored. 1–{@link AGENT_TEAM_NAME_MAX} characters after the
 * Gateway trims it, and unique case-insensitively within the Gateway. Writers
 * should send an already-trimmed value: a name whose untrimmed length exceeds
 * the cap is rejected at this seam before the trim rule is ever consulted.
 */
export const AgentTeamNameSchema = z.string().min(1).max(AGENT_TEAM_NAME_MAX);

/** Optional prose. Absent and `""` both mean "no description". Trimmed length is capped. */
export const AgentTeamDescriptionSchema = z.string().max(AGENT_TEAM_DESCRIPTION_MAX);

export const AgentTeamSchema = z
  .object({
    id: AgentTeamIdSchema,
    name: AgentTeamNameSchema,
    /** Absent when the team has no description; never `""`. */
    description: AgentTeamDescriptionSchema.optional(),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
  })
  .strict();

export const AgentTeamsListPayloadSchema = EmptyPayloadSchema;
/**
 * Team records only — no member counts. Callers derive counts from the agent
 * list they already hold, which keeps a viewer's counts scoped to the agents
 * that viewer can see rather than leaking the existence of inaccessible ones.
 */
export const AgentTeamsListDataSchema = z.object({ teams: z.array(AgentTeamSchema) }).strict();

export const AgentTeamsCreatePayloadSchema = z
  .object({
    name: AgentTeamNameSchema,
    description: AgentTeamDescriptionSchema.optional(),
  })
  .strict();
export const AgentTeamsCreateDataSchema = z.object({ team: AgentTeamSchema }).strict();

export const AgentTeamsUpdatePayloadSchema = z
  .object({
    agentTeamId: AgentTeamIdSchema,
    name: AgentTeamNameSchema.optional(),
    /** `""` clears the description; omitting the key leaves it unchanged. */
    description: AgentTeamDescriptionSchema.optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.name === undefined && value.description === undefined) {
      ctx.addIssue({
        code: "custom",
        message: "team update requires at least one mutable field",
      });
    }
  });
export const AgentTeamsUpdateDataSchema = z.object({ team: AgentTeamSchema }).strict();

export const AgentTeamsDeletePayloadSchema = z.object({ agentTeamId: AgentTeamIdSchema }).strict();
export const AgentTeamsDeleteDataSchema = OkDataSchema;

export type AgentTeam = z.infer<typeof AgentTeamSchema>;
export type AgentTeamsCreatePayload = z.infer<typeof AgentTeamsCreatePayloadSchema>;
export type AgentTeamsUpdatePayload = z.infer<typeof AgentTeamsUpdatePayloadSchema>;
